import React, { useMemo, useRef, useState, useEffect } from 'react';

interface BrandingPreviewProps {
  branding: any;
}

// US Letter size: 8.5" x 11" at 96 DPI (CSS px standard)
const LETTER_WIDTH_IN = 8.5;
const LETTER_HEIGHT_IN = 11;
const DPI = 96;

// Base page dimensions at 1:1 scale (816px × 1056px)
const BASE_PAGE_WIDTH = LETTER_WIDTH_IN * DPI;
const BASE_PAGE_HEIGHT = LETTER_HEIGHT_IN * DPI;

const DEFAULTS = {
  Enabled: false,
  Typography: {
    FontType: 'Times New Roman',
    DocumentFontSize: 12,
    HeadingFontSize: 'auto',
    Levels: { H1: {}, H2: {}, H3: {}, H4: {}, Body: {} },
  },
  Margins: { Top: 1, Bottom: 1, Left: 1, Right: 1 },
  TableOfContents: {
    Enabled: false,
    Depth: 3,
    Style: 'dotted',
  },
  Logos: {
    UseFirmLogo: false,
    FirmLogo: null,
    FirmLogoSize: 24,
    Color: true,
    UseWatermark: false,
    WatermarkLogo: null,
    WatermarkText: '',
    WatermarkOpacity: 25,
  },
  HeaderFooter: {
    HeaderMode: 'text',
    FooterMode: 'text',
    // Per-position modes
    HeaderLeftMode: 'none' as 'none' | 'text' | 'logo',
    HeaderCenterMode: 'none' as 'none' | 'text' | 'logo',
    HeaderRightMode: 'none' as 'none' | 'text' | 'logo',
    FooterLeftMode: 'none' as 'none' | 'text' | 'logo',
    FooterCenterMode: 'none' as 'none' | 'text' | 'logo',
    FooterRightMode: 'text' as 'none' | 'text' | 'logo',
    // Per-position images
    HeaderLeftImage: null as string | File | null,
    HeaderCenterImage: null as string | File | null,
    HeaderRightImage: null as string | File | null,
    FooterLeftImage: null as string | File | null,
    FooterCenterImage: null as string | File | null,
    FooterRightImage: null as string | File | null,
    HeaderTextLeft: '',
    HeaderTextCenter: '',
    HeaderTextRight: '',
    FooterTextLeft: '',
    FooterTextCenter: '',
    FooterTextRight: 'Page {page}',
    HeaderFontType: '',
    FooterFontType: '',
    HeaderFontSize: 10,
    FooterFontSize: 10,
    HeaderLogoSize: 20,
    FooterLogoSize: 18,
  },
};

const sizeLadderPt: Record<'H1' | 'H2' | 'H3' | 'H4', number> = {
  H1: 18, H2: 14, H3: 12, H4: 11,
};

/**
 * Map non-web-safe fonts to CSS font-family stacks with proper fallbacks.
 * Aptos / Aptos Display are Microsoft 365 proprietary fonts — they won't
 * render in browsers unless the user has them installed locally, so we
 * provide the closest web-safe fallback in the stack.
 */
const FONT_FALLBACK_MAP: Record<string, string> = {
  'Aptos':            '"Aptos", "Calibri", "Helvetica Neue", Helvetica, Arial, sans-serif',
  'Aptos Display':    '"Aptos Display", "Cambria", "Georgia", "Times New Roman", serif',
  'Garamond':         '"EB Garamond", "Garamond", "Georgia", serif',
  'Calibri':          '"Calibri", "Helvetica Neue", Helvetica, Arial, sans-serif',
  'Cambria':          '"Cambria", "Georgia", "Times New Roman", serif',
  'Century Schoolbook':'"Century Schoolbook", "Georgia", serif',
  'Palatino Linotype':'"Palatino Linotype", "Palatino", "Book Antiqua", "Georgia", serif',
  'Book Antiqua':     '"Book Antiqua", "Palatino Linotype", "Georgia", serif',
  'Franklin Gothic':  '"Franklin Gothic Medium", "Arial", sans-serif',
  'Trebuchet MS':     '"Trebuchet MS", "Helvetica Neue", Arial, sans-serif',
};

const resolveFontFamily = (fontName: string | undefined, fallbackFont?: string): string => {
  if (!fontName) return fallbackFont ? resolveFontFamily(fallbackFont) : '"Times New Roman", Times, serif';
  // Check the fallback map first (case-insensitive match)
  const mapped = FONT_FALLBACK_MAP[fontName] || 
    Object.entries(FONT_FALLBACK_MAP).find(([k]) => k.toLowerCase() === fontName.toLowerCase())?.[1];
  if (mapped) return mapped;
  // Generic: wrap in quotes and add a generic family
  const lc = fontName.toLowerCase();
  const isSerif = ['times', 'georgia', 'garamond', 'palatino', 'cambria', 'baskerville', 'bookman', 'century'].some(s => lc.includes(s));
  const generic = isSerif ? 'serif' : 'sans-serif';
  return `"${fontName}", ${generic}`;
};

const BrandingPreview: React.FC<BrandingPreviewProps> = ({ branding }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  // Responsive: measure container width and compute scale
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const availableWidth = entry.contentRect.width - 16; // 8px padding each side
        const newScale = Math.min(availableWidth / BASE_PAGE_WIDTH, 1);
        setScale(Math.max(newScale, 0.3)); // minimum 30% scale
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const b = useMemo(() => {
    if (!branding || !branding.Enabled) return DEFAULTS;
    return {
      ...DEFAULTS,
      ...branding,
      Typography: {
        ...DEFAULTS.Typography,
        ...(branding.Typography || {}),
        Levels: { ...DEFAULTS.Typography.Levels, ...(branding.Typography?.Levels || {}) },
      },
      Margins: { ...DEFAULTS.Margins, ...(branding.Margins || {}) },
      TableOfContents: { ...DEFAULTS.TableOfContents, ...(branding.TableOfContents || {}) },
      Logos: { ...DEFAULTS.Logos, ...(branding.Logos || {}) },
      HeaderFooter: { ...DEFAULTS.HeaderFooter, ...(branding.HeaderFooter || {}) },
    };
  }, [branding]);

  // Convert inches to pixels at base scale (applied at the page level via CSS transform)
  const inToPx = (inches: number) => inches * DPI;
  
  // Convert points to pixels (1pt = 1/72 inch)
  const ptToPx = (pt: number) => (pt / 72) * DPI;

  const firmLogoUrl = useMemo(() => {
    const src = b.Logos?.FirmLogo;
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  }, [b.Logos?.FirmLogo]);

  const watermarkLogoUrl = useMemo(() => {
    const src = b.Logos?.WatermarkLogo;
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  }, [b.Logos?.WatermarkLogo]);

  // Helper to get URL from string or File
  const getImageUrl = (src: string | File | null | undefined): string => {
    if (!src) return '';
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  };

  // Per-position image URLs
  const headerLeftImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderLeftImage), [b.HeaderFooter?.HeaderLeftImage]);
  const headerCenterImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderCenterImage), [b.HeaderFooter?.HeaderCenterImage]);
  const headerRightImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderRightImage), [b.HeaderFooter?.HeaderRightImage]);
  const footerLeftImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterLeftImage), [b.HeaderFooter?.FooterLeftImage]);
  const footerCenterImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterCenterImage), [b.HeaderFooter?.FooterCenterImage]);
  const footerRightImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterRightImage), [b.HeaderFooter?.FooterRightImage]);

  // Calculate content area dimensions based on margins
  const marginTop = inToPx(b.Margins?.Top ?? 1);
  const marginBottom = inToPx(b.Margins?.Bottom ?? 1);
  const marginLeft = inToPx(b.Margins?.Left ?? 1);
  const marginRight = inToPx(b.Margins?.Right ?? 1);
  const contentWidth = BASE_PAGE_WIDTH - marginLeft - marginRight;
  const contentHeight = BASE_PAGE_HEIGHT - marginTop - marginBottom;

  // Get font size in pixels for a level
  const getFontSizePx = (level: 'H1' | 'H2' | 'H3' | 'H4' | 'Body'): number => {
    const lvl = b.Typography?.Levels?.[level] || {};
    let sizePt: number;

    if (typeof lvl.Size === 'number') {
      sizePt = lvl.Size;
    } else if (typeof lvl.Size === 'string' && lvl.Size.toLowerCase() !== 'auto') {
      const n = Number(lvl.Size);
      sizePt = isNaN(n) ? (level === 'Body' ? 12 : sizeLadderPt[level as keyof typeof sizeLadderPt]) : n;
    } else {
      sizePt = level === 'Body' 
        ? Number(b.Typography?.DocumentFontSize) || 12 
        : sizeLadderPt[level as keyof typeof sizeLadderPt] || 12;
    }
    return ptToPx(sizePt);
  };

  const getLevelStyle = (level: 'H1' | 'H2' | 'H3' | 'H4' | 'Body'): React.CSSProperties => {
    const lvl = b.Typography?.Levels?.[level] || {};
    const fontFamily = resolveFontFamily(lvl.FontType, b.Typography?.FontType);
    const fontSize = getFontSizePx(level);
    const fontWeight = level === 'Body' ? 'normal' : 'bold';
    const color = lvl.Color || (level === 'Body' ? '#000' : '#000');
    
    return {
      fontFamily,
      fontSize: `${fontSize}px`,
      fontWeight,
      color,
      lineHeight: 1.4,
      margin: 0,
    };
  };

  const getHeaderStyle = (): React.CSSProperties => ({
    fontFamily: resolveFontFamily(b.HeaderFooter?.HeaderFontType, b.Typography?.FontType),
    fontSize: `${ptToPx(b.HeaderFooter?.HeaderFontSize || 10)}px`,
    color: '#666',
  });

  const getFooterStyle = (): React.CSSProperties => ({
    fontFamily: resolveFontFamily(b.HeaderFooter?.FooterFontType, b.Typography?.FontType),
    fontSize: `${ptToPx(b.HeaderFooter?.FooterFontSize || 10)}px`,
    color: '#666',
  });

  const grayscale = b.Logos?.Color === false ? 'grayscale(100%)' : 'none';

  // Document Page Component
  const DocumentPage: React.FC<{ pageNum: number; children: React.ReactNode; showGuides?: boolean }> = ({ pageNum, children, showGuides = true }) => {
    // Per-position modes (fallback to legacy global modes for backwards compatibility)
    const headerLeftMode = b.HeaderFooter?.HeaderLeftMode || 'none';
    const headerCenterMode = b.HeaderFooter?.HeaderCenterMode || 'none';
    const headerRightMode = b.HeaderFooter?.HeaderRightMode || 'none';
    const footerLeftMode = b.HeaderFooter?.FooterLeftMode || 'none';
    const footerCenterMode = b.HeaderFooter?.FooterCenterMode || 'none';
    const footerRightMode = b.HeaderFooter?.FooterRightMode || 'text';

    // Get three-position text values with {page} placeholder replacement
    const headerTextLeft = b.HeaderFooter?.HeaderTextLeft || '';
    const headerTextCenter = b.HeaderFooter?.HeaderTextCenter || '';
    const headerTextRight = b.HeaderFooter?.HeaderTextRight || '';
    const footerTextLeft = (b.HeaderFooter?.FooterTextLeft || '').replace('{page}', String(pageNum));
    const footerTextCenter = (b.HeaderFooter?.FooterTextCenter || '').replace('{page}', String(pageNum));
    const footerTextRight = (b.HeaderFooter?.FooterTextRight || 'Page {page}').replace('{page}', String(pageNum));
    
    // Check if any position has content
    const hasHeaderContent = headerLeftMode !== 'none' || headerCenterMode !== 'none' || headerRightMode !== 'none';
    const hasFooterContent = footerLeftMode !== 'none' || footerCenterMode !== 'none' || footerRightMode !== 'none';
    
    // Helper to get image URL for a position, fallback to firm logo
    const getPositionImageUrl = (positionImageUrl: string): string => {
      if (positionImageUrl) return positionImageUrl;
      if (b.Logos?.UseFirmLogo && firmLogoUrl) return firmLogoUrl;
      return '';
    };
    
    // Margin guide styles - subtle gray/black
    const guideLineStyle: React.CSSProperties = {
      position: 'absolute',
      borderColor: '#9ca3af',
      borderStyle: 'dashed',
      opacity: 0.4,
      pointerEvents: 'none',
    };
    
    const marginLabelStyle: React.CSSProperties = {
      position: 'absolute',
      fontSize: '7px',
      color: '#6b7280',
      fontFamily: 'system-ui, sans-serif',
      fontWeight: 500,
      opacity: 0.7,
      pointerEvents: 'none',
    };

    return (
      <div
        className="relative flex-shrink-0"
        style={{
          width: `${BASE_PAGE_WIDTH * scale}px`,
          height: `${BASE_PAGE_HEIGHT * scale}px`,
        }}
      >
        <div
          className="absolute bg-white origin-top-left"
          style={{
            width: `${BASE_PAGE_WIDTH}px`,
            height: `${BASE_PAGE_HEIGHT}px`,
            transform: `scale(${scale})`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
          }}
        >
        {/* Watermark */}
        {b.Logos?.UseWatermark && (
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{ opacity: (b.Logos?.WatermarkOpacity ?? 25) / 100 }}
          >
            {watermarkLogoUrl ? (
              <img 
                src={watermarkLogoUrl} 
                alt="Watermark" 
                className="max-w-[60%] max-h-[60%] object-contain"
                style={{ filter: grayscale }}
              />
            ) : (
              <span 
                className="font-bold -rotate-45 select-none whitespace-nowrap"
                style={{ 
                  fontFamily: resolveFontFamily(b.Typography?.FontType),
                  fontSize: `${ptToPx(72)}px`,
                  color: '#ccc',
                }}
              >
                {b.Logos?.WatermarkText || 'DRAFT'}
              </span>
            )}
          </div>
        )}

        {/* Margin Guides */}
        {showGuides && (
          <>
            {/* Top margin line */}
            <div style={{ ...guideLineStyle, top: marginTop, left: 0, right: 0, borderTopWidth: 1 }} />
            {/* Bottom margin line */}
            <div style={{ ...guideLineStyle, bottom: marginBottom, left: 0, right: 0, borderTopWidth: 1 }} />
            {/* Left margin line */}
            <div style={{ ...guideLineStyle, left: marginLeft, top: 0, bottom: 0, borderLeftWidth: 1 }} />
            {/* Right margin line */}
            <div style={{ ...guideLineStyle, right: marginRight, top: 0, bottom: 0, borderLeftWidth: 1 }} />
            
            {/* Margin dimension labels */}
            <span style={{ ...marginLabelStyle, top: marginTop / 2 - 4, left: '50%', transform: 'translateX(-50%)' }}>
              {b.Margins?.Top ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, bottom: marginBottom / 2 - 4, left: '50%', transform: 'translateX(-50%)' }}>
              {b.Margins?.Bottom ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, left: marginLeft / 2 - 6, top: '50%', transform: 'translateY(-50%)' }}>
              {b.Margins?.Left ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, right: marginRight / 2 - 6, top: '50%', transform: 'translateY(-50%)' }}>
              {b.Margins?.Right ?? 1}"
            </span>
          </>
        )}

        {/* Header - three-column layout in top margin */}
        {hasHeaderContent && (
          <div
            className="absolute flex items-center justify-between"
            style={{
              top: inToPx(0.5),
              left: marginLeft,
              width: contentWidth,
            }}
          >
            {/* Left section */}
            <div className="flex items-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerLeftMode === 'logo' && getPositionImageUrl(headerLeftImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerLeftImageUrl)} 
                  alt="Header Left" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerLeftMode === 'text' && headerTextLeft && (
                <span style={getHeaderStyle()}>{headerTextLeft}</span>
              )}
            </div>
            
            {/* Center section */}
            <div className="flex items-center justify-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerCenterMode === 'logo' && getPositionImageUrl(headerCenterImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerCenterImageUrl)} 
                  alt="Header Center" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerCenterMode === 'text' && headerTextCenter && (
                <span style={getHeaderStyle()}>{headerTextCenter}</span>
              )}
            </div>
            
            {/* Right section */}
            <div className="flex items-center justify-end" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerRightMode === 'logo' && getPositionImageUrl(headerRightImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerRightImageUrl)} 
                  alt="Header Right" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerRightMode === 'text' && headerTextRight && (
                <span style={getHeaderStyle()}>{headerTextRight}</span>
              )}
            </div>
          </div>
        )}

        {/* Footer - three-column layout in bottom margin */}
        {hasFooterContent && (
          <div
            className="absolute flex items-center justify-between"
            style={{
              bottom: inToPx(0.5),
              left: marginLeft,
              width: contentWidth,
            }}
          >
            {/* Left section */}
            <div className="flex items-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerLeftMode === 'logo' && getPositionImageUrl(footerLeftImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerLeftImageUrl)} 
                  alt="Footer Left" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerLeftMode === 'text' && footerTextLeft && (
                <span style={getFooterStyle()}>{footerTextLeft}</span>
              )}
            </div>
            
            {/* Center section */}
            <div className="flex items-center justify-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerCenterMode === 'logo' && getPositionImageUrl(footerCenterImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerCenterImageUrl)} 
                  alt="Footer Center" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerCenterMode === 'text' && footerTextCenter && (
                <span style={getFooterStyle()}>{footerTextCenter}</span>
              )}
            </div>
            
            {/* Right section */}
            <div className="flex items-center justify-end" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerRightMode === 'logo' && getPositionImageUrl(footerRightImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerRightImageUrl)} 
                  alt="Footer Right" 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerRightMode === 'text' && footerTextRight && (
                <span style={getFooterStyle()}>{footerTextRight}</span>
              )}
            </div>
          </div>
        )}

        {/* Content area */}
        <div
          className="absolute overflow-hidden"
          style={{
            top: marginTop,
            left: marginLeft,
            width: contentWidth,
            height: contentHeight,
            backgroundColor: showGuides ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
          }}
        >
          {children}
        </div>
        </div>
      </div>
    );
  };

  // Spacing helper
  const spacing = (multiplier: number = 1) => ({ marginBottom: `${ptToPx(6) * multiplier}px` });

  return (
    <div ref={containerRef} className="w-full">
  
      <div className="flex flex-col items-center gap-4 pb-4">
        {/* Main content page */}
        <DocumentPage pageNum={1}>
          <div style={{ padding: `${ptToPx(2)}px 0` }}>
            {/* Article Heading */}
            <h1 style={{ ...getLevelStyle('H1'), ...spacing(1.5) }}>
              Article I: Definitions
            </h1>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5), textAlign: 'justify' }}>
              For purposes of this Agreement, the following terms shall have the meanings set forth below. Unless otherwise specified, all references to sections shall refer to sections of this Agreement.
            </p>

            {/* Section */}
            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              1.1 Key Terms
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5), textAlign: 'justify' }}>
              "Confidential Information" means any non-public information disclosed by either party to the other, whether orally or in writing, that is designated as confidential or that reasonably should be understood to be confidential.
            </p>

            {/* Subsection */}
            <h3 style={{ ...getLevelStyle('H3'), ...spacing(1), marginTop: `${ptToPx(10)}px` }}>
              1.1.1 Definition Categories
            </h3>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1), textAlign: 'justify' }}>
              The following categories of information shall be deemed Confidential Information without requiring explicit designation: trade secrets, customer lists, pricing information, and proprietary methodologies.
            </p>

            {/* Sub-subsection */}
            <h4 style={{ ...getLevelStyle('H4'), ...spacing(0.5), marginTop: `${ptToPx(8)}px` }}>
              (a) Trade Secrets
            </h4>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1), textAlign: 'justify' }}>
              Information that derives independent economic value from not being generally known and is subject to reasonable efforts to maintain its secrecy.
            </p>

            <h4 style={{ ...getLevelStyle('H4'), ...spacing(0.5), marginTop: `${ptToPx(8)}px` }}>
              (b) Proprietary Data
            </h4>
            
            <p style={{ ...getLevelStyle('Body'), textAlign: 'justify' }}>
              All business, technical, and financial information developed by or for the disclosing party that provides a competitive advantage in the marketplace.
            </p>
          </div>
        </DocumentPage>

        {/* Second page - shows continuation and footer working */}
        <DocumentPage pageNum={2}>
          <div style={{ padding: `${ptToPx(2)}px 0` }}>
            <h1 style={{ ...getLevelStyle('H1'), ...spacing(1.5) }}>
              Article II: Obligations
            </h1>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5), textAlign: 'justify' }}>
              Each party agrees to maintain the confidentiality of all Confidential Information received from the other party and to use such information solely for the purposes contemplated by this Agreement.
            </p>

            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              2.1 Non-Disclosure
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5), textAlign: 'justify' }}>
              Neither party shall disclose any Confidential Information to any third party without the prior written consent of the disclosing party, except as may be required by law or regulation.
            </p>

            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              2.2 Permitted Disclosures
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1), textAlign: 'justify' }}>
              Notwithstanding the foregoing, either party may disclose Confidential Information to its employees, contractors, and advisors who have a need to know such information.
            </p>

            <h3 style={{ ...getLevelStyle('H3'), ...spacing(1), marginTop: `${ptToPx(10)}px` }}>
              2.2.1 Employee Obligations
            </h3>
            
            <p style={{ ...getLevelStyle('Body'), textAlign: 'justify' }}>
              All such employees, contractors, and advisors must be bound by confidentiality obligations no less restrictive than those contained herein.
            </p>
          </div>
        </DocumentPage>
      </div>
    </div>
  );
};

export default BrandingPreview;
